import React, { useEffect, useRef, useState } from 'react';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from "framer-motion";
import { Heart, BookOpen, Star } from 'lucide-react';
import { Ebook, EbookCategory } from '@/types/types';

import { useEbooks } from '@/context/EbookContext';
import { Helmet } from 'react-helmet';


const categories: EbookCategory[] = [
  "Toutes",
  "Web",
  "Mobile",
  "Desktop",
  "AI",
];

const Ebooks = () => {
  const [selectedCategory, setSelectedCategory] = useState<EbookCategory>("Toutes");
  const [selectedEbook, setSelectedEbook] = useState<Ebook | null>(null);
  const [visibleItems, setVisibleItems] = useState(4);
  

    // Use our context to access ebooks data
    const { 
      ebooks, 
      favoriteEbooks, 
      isLoading, 
      error, 
      toggleFavorite,
 //     getEbooksByCategory,
      isCategoryLoading
    } = useEbooks();
  
// Get ebooks by category
//const filteredEbooks = getEbooksByCategory(selectedCategory);

// Recommended e-books (top rated)
const recommendedEbooks = ebooks
  ? [...ebooks].sort((a, b) => b.rating - a.rating).slice(0, 4)
  : [];


  // Refs for scroll animations
  const heroRef = useRef(null);
  const categoriesRef = useRef(null);
  const recommendedRef = useRef(null);
  const ebookRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Handle scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate__animated', 'animate__fadeInUp');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    // Observe hero section
    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    // Observe categories section
    if (categoriesRef.current) {
      observer.observe(categoriesRef.current);
    }

    // Observe recommended section
    if (recommendedRef.current) {
      observer.observe(recommendedRef.current);
    }

    // Observe each ebook card with staggered delay
    ebookRefs.current.forEach((ref, index) => {
      if (ref) {
        setTimeout(() => {
          observer.observe(ref);
        }, index * 100);
      }
    });

    return () => observer.disconnect();
  }, []);

  // Load more books on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 500) {
        if (ebooks && visibleItems < ebooks.length) {
          setVisibleItems(prev => Math.min(prev + 4, ebooks.length));
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [visibleItems, ebooks.length, ebooks]);
  // Get favorite status
  const isFavorite = (id: number) => {
    return favoriteEbooks?.some(book => book.id === id) || false;
  };

  // Handle toggle favorite with our context
  const handleToggleFavorite = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(id);
  };

  return (
    <>
      <Helmet>
        <title>Mopaossi | Documents</title>
        <meta
          name="description"
          content="Développeur Frontend | No-code| Low-code | UI/UX | Agilité"
        />
        <meta property="og:title" content="Mopaossi | Développeur Créatif" />
        <meta
          property="og:description"
          content="Découvrer un créateur, digitalisation, tech, app, dev : Mes sujets préférés."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://cbenji.pages.dev" />
      </Helmet>
    <PageLayout
      title="Mopaossi | E-Books"
      description="Découvrez ma collection premium d'e-books soigneusement sélectionnés pour vous inspirer et vous faire grandir."
    >
      {/* Hero Section with 3D Image */}
      <section 
        ref={heroRef} 
        className="flex flex-col-reverse md:flex-row items-center justify-between gap-10 py-16 md:py-24"
      >
        {/* Left: Text */}
        <div className="w-full md:w-1/2">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-400 to-blue-700 animate-gradient">
              Ma Bibliothèque Premium
            </span>
          </h1>
          <p className="text-lg md:text-xl text-gray-700 mb-8">
            Découvrez une sélection exclusive d'e-books pour booster vos connaissances en Dev. Chaque livre est une documentation complète pour setup les projects que j'ai déjà réalisé.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button className="bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-800 hover:to-blue-950 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
              Explorer la collection
            </Button>
            <Button 
              variant="outline" 
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-300"
              onClick={() => setSelectedCategory("Toutes")}
            >
              Mes favoris
            </Button>
          </div>
        </div>
        {/* Right: 3D Image */}
        <div className="w-full md:w-1/2 flex justify-center items-center">
          <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-2xl shadow-xl bg-gradient-to-br from-blue-100 via-purple-100 to-white flex items-center justify-center">
            <img 
              src="https://hynmctzoemxttjsmvjyz.supabase.co/storage/v1/object/public/images//playground.png" 
              alt="E-books 3D illustration" 
              className="w-56 h-56 md:w-72 md:h-72 object-contain"
            />
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-32 h-4 bg-gradient-to-r from-blue-300 via-purple-300 to-blue-300 rounded-full blur-md opacity-40"></div>
          </div>
        </div>
      </section>

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 my-6 rounded-r">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                Une erreur est survenue lors du chargement des e-books. Veuillez réessayer plus tard.
              </p>
            </div>
          </div>
        </div>
      )}

      {!isLoading && !error && ebooks && (
        <>
          {/* Recommended Section */}
          <section 
            ref={recommendedRef}
            className="mb-16 opacity-0"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Recommandés</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {recommendedEbooks.map((book) => (
                <motion.div
                  key={`recommended-${book.id}`}
                  className="relative bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer"
                  whileHover={{ scale: 1.05, boxShadow: "0 8px 32px rgba(80,80,200,0.12)" }}
                  onClick={() => setSelectedEbook(book)}
                >
                  <div className="relative">
                    <img src={book.cover} alt={book.title} className="w-full h-56 object-cover" />
                    <button 
                      className={`absolute top-2 right-2 p-2 rounded-full ${isFavorite(book.id) ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-500'}`}
                      onClick={(e) => handleToggleFavorite(book.id, e)}
                    >
                      <Heart size={16} fill={isFavorite(book.id) ? "white" : "none"} />
                    </button>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg truncate">{book.title}</h3>
                    <p className="text-gray-500 text-sm truncate">{book.author}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Categories */}
          <section 
            ref={categoriesRef}
            className="mb-8 opacity-0"
          >
            <h2 className="text-2xl font-bold mb-4">Catégories</h2>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === cat
                      ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:shadow-sm"
                  }`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </section>

          {/* Category loading state */}
          {isCategoryLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}

          {/* E-books Grid with Infinite Scroll */}
          {!isCategoryLoading && ebooks && (
            <section className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {ebooks.slice(0, visibleItems).map((book, idx) => (
                <motion.div
                  key={book.id}
                  ref={el => ebookRefs.current[idx] = el}
                  className="relative bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer opacity-0"
                  whileHover={{ scale: 1.05, boxShadow: "0 8px 32px rgba(80,80,200,0.12)" }}
                  onClick={() => setSelectedEbook(book)}
                >
                  <div className="relative">
                    <img src={book.cover} alt={book.title} className="w-full h-56 object-cover" />
                    <button 
                      className={`absolute top-2 right-2 p-2 rounded-full ${isFavorite(book.id) ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-500'}`}
                      onClick={(e) => handleToggleFavorite(book.id, e)}
                    >
                      <Heart size={16} fill={isFavorite(book.id) ? "white" : "none"} />
                    </button>
                    <div className="absolute top-2 left-2 bg-white/90 rounded-full px-3 py-1 text-xs font-semibold shadow flex items-center gap-1">
                      <Star size={12} fill="gold" stroke="gold" />
                      <span>{book.rating}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg truncate">{book.title}</h3>
                    <p className="text-gray-500 text-sm truncate">{book.author}</p>
                  </div>
                </motion.div>
              ))}
            </section>
          )}

          {/* Load More Button (shown only if there are more books to load) */}
          {ebooks && visibleItems < ebooks.length && (
            <div className="flex justify-center mb-16">
              <Button 
                onClick={() => setVisibleItems(prev => Math.min(prev + 4, ebooks.length))}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Charger plus
              </Button>
            </div>
          )}

          {/* E-book Detail Pop-up */}
          <AnimatePresence>
            {selectedEbook && (
              <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/40 backdrop-blur-sm">
                <motion.div
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-gradient-to-b from-blue-600 to-blue-650 text-white rounded-l-2xl shadow-2xl p-8 max-w-md w-full h-full overflow-y-auto relative"
                >
                  <button
                    className="absolute top-4 right-4 text-white/70 hover:text-white text-2xl"
                    onClick={() => setSelectedEbook(null)}
                    aria-label="Fermer"
                  >
                    ×
                  </button>
                  
                  <div className="flex flex-col items-center text-center h-full">
                    <div className="mt-8 mb-6">
                      <img 
                        src={selectedEbook.cover} 
                        alt={selectedEbook.title} 
                        className="w-40 h-56 object-cover mx-auto rounded-lg shadow-lg" 
                      />
                    </div>
                    
                    <h2 className="text-2xl font-bold mb-1">{selectedEbook.title}</h2>
                    <p className="text-white/80 mb-6">{selectedEbook.author}</p>
                    
                    <div className="flex justify-center gap-8 mb-8">
                      <div className="flex flex-col items-center">
                        <span className="text-lg font-bold">{selectedEbook.pages}</span>
                        <span className="text-xs text-white/70">Pages</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-lg font-bold">{selectedEbook.rating}</span>
                        <span className="text-xs text-white/70">Ratings</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-lg font-bold">{selectedEbook.reviews}</span>
                        <span className="text-xs text-white/70">Reviews</span>
                      </div>
                    </div>
                    
                    <div className="text-left mb-8">
                      <p className="text-white/90 leading-relaxed">
                        {selectedEbook.description}
                      </p>
                    </div>
                    
                    <div className="mt-auto w-full">
                      <Button 
                        className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                      >
                        <BookOpen size={20} />
                        Lire
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </>
      )}
    </PageLayout>
    </>

  );
};

export default Ebooks;